import React, { useCallback, useEffect, Suspense } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

// Always-eager: guards, layout, error boundary, analytics
import AdminGuard from './utils/AdminGuard.jsx';
import AuthGuard from './utils/AuthGuard.jsx';
import ErrorPage from './pages/Errors/ErrorPage.jsx';
import StartLayout from './pages/Layouts/StartLayout';
import ReactGA from 'react-ga4';
import { trackPageView, setUserProperties } from './utils/analytics';
import { restoreCurrency } from './store/currencySlice.jsx';
import { restoreUser } from './store/userSlice';
import { setCategories } from '@/store/categorySlice.jsx';
import { useDispatch } from 'react-redux';
import ProductService from '@/services/ProductService.js';

// Lazy-loaded pages – split into separate chunks for faster initial load
const AboutPage = React.lazy(() => import('./pages/About/AboutPage.jsx'));
const ProductPage = React.lazy(() => import('./pages/Ads/ProductPage.jsx'));
const CategoryProductPage = React.lazy(() => import('./pages/Ads/CategoryProductPage.jsx'));
const AddAdsPage = React.lazy(() => import('./pages/Dashboard/pages/AddAds/AddAdsPage.jsx'));
const AdsListPage = React.lazy(() => import('./pages/Dashboard/pages/AdsList/AdsListPage'));
const BannersPage = React.lazy(() => import('./pages/Dashboard/pages/Banners/BannersPage.jsx'));
const BlogEditDashboardPage = React.lazy(() => import('./pages/Dashboard/pages/Blog/BlogEditDashboardPage.jsx'));
const BlogListDashboardPage = React.lazy(() => import('./pages/Dashboard/pages/Blog/BlogListDashboardPage.jsx'));
const BlogListPage = React.lazy(() => import('./pages/Blog').then((m) => ({ default: m.BlogListPage })));
const BlogSinglePage = React.lazy(() => import('./pages/Blog').then((m) => ({ default: m.BlogSinglePage })));
const ChangePasswordPage = React.lazy(() => import('./pages/Password/ChangePasswordPage.jsx'));
const ContactPage = React.lazy(() => import('./pages/Contact/ContactPage.jsx'));
const DetailsPage = React.lazy(() => import('./pages/Details/DetailsPage.jsx'));
const LegacyAdRedirect = React.lazy(() => import('./pages/Details/LegacyAdRedirect.jsx'));
const HomePage = React.lazy(() => import('./pages/Home/HomePage.jsx'));
const HomeSliderPage = React.lazy(() => import('./pages/Dashboard/pages/Banners/pages/HomeSliderPage.jsx'));
const InstructionPage = React.lazy(() => import('./pages/Dashboard/pages/Instruction/InstructionPage.jsx'));
const LearnMorePage = React.lazy(() => import('./pages/LearnMore/LearnMorePage.jsx'));
const LegalEntitiesPage = React.lazy(() => import('./pages/Dashboard/pages/Settings/pages/LegalEntities/LegalEntitiesPage.jsx'));
const LoginPage = React.lazy(() => import('./pages/Login/LoginPage'));
const NewPasswordPage = React.lazy(() => import('./pages/Password/NewPasswordPage.jsx'));
const PasswordPage = React.lazy(() => import('./pages/Dashboard/pages/Settings/pages/Password/PasswordPage.jsx'));
const PaymentsPage = React.lazy(() => import('./pages/Dashboard/pages/Payments/PaymentsPage.jsx'));
const PaymentResult = React.lazy(() => import('./pages/Payment/PaymentResult.jsx'));
const PriceListPage = React.lazy(() => import('./components/footer/pages/PriceListPage.jsx'));
const PrivacyPolicyPage = React.lazy(() => import('./components/footer/pages/PrivacyPolicyPage.jsx'));
const ProfilePage = React.lazy(() => import('./pages/Dashboard/pages/Settings/pages/Profile/ProfilePage.jsx'));
const RealEstateSoonPage = React.lazy(() => import('./pages/RealEstate/RealEstateSoonPage.jsx'));
const RegisterPage = React.lazy(() => import('./pages/Register/RegisterPage'));
const SavedAdsPage = React.lazy(() => import('./pages/SavedAds/SavedAdsPage.jsx'));
const SettingsPage = React.lazy(() => import('./pages/Dashboard/pages/Settings/SettingsPage.jsx'));
const SliderPage = React.lazy(() => import('./pages/Dashboard/pages/Banners/pages/SliderPage.jsx'));
const StepFourPage = React.lazy(() => import('./pages/Dashboard/pages/AddAds/pages/StepFour/StepFourPage.jsx'));
const StepOne = React.lazy(() => import('./pages/Dashboard/pages/Payments/pages/StepOne.jsx'));
const StepOnePage = React.lazy(() => import('./pages/Dashboard/pages/AddAds/pages/StepOne/StepOnePage.jsx'));
const StepThreePage = React.lazy(() => import('./pages/Dashboard/pages/AddAds/pages/StepThree/StepThreePage.jsx'));
const StepTwo = React.lazy(() => import('./pages/Dashboard/pages/Payments/pages/StepTwo.jsx'));
const StepTwoPage = React.lazy(() => import('./pages/Dashboard/pages/AddAds/pages/StepTwo/StepTwoPage.jsx'));
const TermsAndConditionPage = React.lazy(() => import('./components/footer/pages/TermsAndConditionPage.jsx'));
const UserDashboard = React.lazy(() => import('./pages/Dashboard/UserDashboard.jsx'));
const UserPage = React.lazy(() => import('./pages/User/UserPage.jsx'));
const UsersPage = React.lazy(() => import('./pages/Dashboard/pages/Users/UsersPage.jsx'));
const DiscountTiersPage = React.lazy(() => import('./pages/Dashboard/pages/DiscountTiers/DiscountTiersPage.jsx'));

const TRACKING_ID = import.meta.env.VITE_GOOGLE_TRACKING_ID; // OUR_TRACKING_IDs
ReactGA.initialize(TRACKING_ID);

const router = createBrowserRouter([
  {
    path: '/',
    element: <StartLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'learn-more',
        element: <LearnMorePage />,
      },
      {
        path: 'products',
        element: <ProductPage />,
      },
      {
        path: 'products/details/:legacyId',
        element: <LegacyAdRedirect />,
      },
      {
        path: 'products/:categoryName/:subCategoryName/:title',
        element: <DetailsPage />,
      },
      {
        path: 'products/:categoryName?/:subCategoryName?',
        element: <CategoryProductPage />,
      },
      {
        path: 'about',
        element: <AboutPage />,
      },
      {
        path: 'contact',
        element: <ContactPage />,
      },
      {
        path: 'payment/result',
        element: <PaymentResult />,
      },
      {
        path: 'real-estate',
        element: <RealEstateSoonPage />,
      },
      {
        path: 'saved',
        element: (
          <AuthGuard>
            <SavedAdsPage />,
          </AuthGuard>
        ),
      },
      {
        path: 'user-details/:userId',
        element: <UserPage />,
      },
      {
        path: 'blog',
        element: <BlogListPage />,
      },
      {
        path: 'blog/:slug',
        element: <BlogSinglePage />,
      },
    ],
  },
  {
    path: '/dashboard',
    element: (
      <AuthGuard>
        <UserDashboard />
      </AuthGuard>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <AdsListPage />,
      },
      {
        path: 'addAds',
        element: <AddAdsPage />,
        errorElement: <ErrorPage />,
        children: [
          {
            index: true,
            element: <StepOnePage />,
          },
          {
            path: 'stepTwo',
            element: <StepTwoPage />,
          },
          {
            path: 'StepThree',
            element: <StepThreePage />,
          },
          {
            path: 'StepFour',
            element: <StepFourPage />,
          },
        ],
      },
      {
        path: 'payments',
        element: <PaymentsPage />,
        children: [
          {
            index: true,
            element: <StepOne />,
          },
          {
            path: 'step-two',
            element: <StepTwo />,
          },
        ],
      },
      {
        path: 'users',
        element: (
          <AdminGuard>
            <UsersPage />
          </AdminGuard>
        ),
      },
      {
        path: 'banners',
        element: <BannersPage />,
        children: [
          {
            path: 'slider1',
            element: <SliderPage slider='slider1' />,
          },
          {
            path: 'slider2',
            element: <SliderPage slider='slider2' />,
          },
          {
            path: 'slider3',
            element: <SliderPage slider='slider3' />,
          },
          {
            path: 'slider4',
            element: <SliderPage slider='slider4' />,
          },
          {
            path: 'slider5',
            element: <SliderPage slider='slider5' />,
          },
          {
            path: 'homeSliders',
            element: <HomeSliderPage slider='homeSliders' />,
          },
        ],
      },
      {
        path: 'instructions',
        element: <InstructionPage />,
      },
      {
        path: 'settings',
        element: <SettingsPage />,
        children: [
          {
            index: true,

            element: <ProfilePage />,
          },
          {
            path: 'password',
            element: <PasswordPage />,
          },
          {
            path: 'legal-entities',
            element: <LegalEntitiesPage />,
          },
        ],
      },
      {
        path: 'blog',
        element: (
          <AdminGuard>
            <BlogListDashboardPage />
          </AdminGuard>
        ),
      },
      {
        path: 'blog/edit/:id?',
        element: (
          <AdminGuard>
            <BlogEditDashboardPage />
          </AdminGuard>
        ),
      },
      {
        path: 'discount-tiers',
        element: (
          <AdminGuard>
            <DiscountTiersPage />
          </AdminGuard>
        ),
      },
    ],
  },
  { path: 'login', element: <LoginPage /> },
  { path: 'register', element: <RegisterPage /> },
  { path: 'change-password', element: <ChangePasswordPage /> },
  { path: 'new-password', element: <NewPasswordPage /> },
  { path: 'instruction', element: <InstructionPage /> },
  { path: 'ads-list', element: <AdsListPage /> },
  { path: 'terms-and-conditions', element: <TermsAndConditionPage /> },
  { path: 'privacy-policy', element: <PrivacyPolicyPage /> },
  { path: 'price-list', element: <PriceListPage /> },
]);

// Track SPA route changes in GA4 — fires on every navigation (not just initial page load)
router.subscribe((state) => {
  trackPageView(state.location.pathname, state.location.search);
});

const App = () => {
  const dispatch = useDispatch();
  const getCategories = useCallback(async () => {
    const response = await ProductService.getAllCategories();
    dispatch(setCategories(response.data));
  }, [dispatch]);

  useEffect(() => {
    getCategories().finally();
    const restored = dispatch(restoreUser());
    // Set GA4 user-scoped dimension after user is restored from localStorage
    Promise.resolve(restored).then(() => {
      const stored = localStorage.getItem('er_token');
      if (stored) {
        try {
          const payload = JSON.parse(atob(stored.split('.')[1]));
          if (payload?._id) setUserProperties(payload._id, payload.role || '');
        } catch {
          /* ignore malformed token */
        }
      }
    });
    dispatch(restoreCurrency());
  }, [getCategories, dispatch]);

  return (
    <Suspense
      fallback={
        <div className='flex h-screen items-center justify-center'>
          <div className='h-10 w-10 animate-spin rounded-full border-4 border-primary-yellow border-t-transparent' />
        </div>
      }
    >
      <RouterProvider router={router} />
    </Suspense>
  );
};

export default App;
